import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { AuthUser, Empresa, LoginCredentials, UsuarioEmpresaAcesso } from '@/types';
import { authService, empresaService } from '@/services/api';
import { usePushNotifications, removerTokenPushDoAparelho } from '@/hooks/usePushNotifications';

/** `null` representa "Todas as empresas" — só usado pelo superadmin, que não tem vínculo
 *  fixo com nenhuma empresa (ver `empresaAtiva`, exclusivo de admin/comum). Telas que
 *  precisam de uma empresa específica pra funcionar (ex: Organograma, Fluxograma,
 *  Calendário) mostram um aviso pedindo pra escolher uma empresa aqui, em vez de manter
 *  seletor local próprio — só existe este filtro global. */
export type EmpresaFiltroId = string | null;

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  empresaAtiva: UsuarioEmpresaAcesso | null;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  trocarEmpresa: (empresaId: string) => void;
  /** Lista de todas as empresas ativas — só carregada/relevante para superadmin. */
  empresasDisponiveis: Empresa[];
  /** Filtro global de empresa do superadmin (`null` = "Todas as empresas"). Persiste entre navegações/telas. */
  empresaFiltroId: EmpresaFiltroId;
  setEmpresaFiltroId: (empresaId: EmpresaFiltroId) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'okr_auth_token';
const EMPRESA_ATIVA_KEY = 'okr_empresa_ativa';
const EMPRESA_FILTRO_KEY = 'okr_empresa_filtro_superadmin';

function resolveEmpresaAtiva(
  user: AuthUser,
  savedId: string | null
): UsuarioEmpresaAcesso | null {
  if (!user.empresas || user.empresas.length === 0) return null;
  if (savedId) {
    const found = user.empresas.find((e) => e.id === savedId);
    if (found) return found;
  }
  return user.empresas[0];
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [empresaAtiva, setEmpresaAtiva] = useState<UsuarioEmpresaAcesso | null>(null);
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState<Empresa[]>([]);
  const [empresaFiltroId, setEmpresaFiltroIdState] = useState<EmpresaFiltroId>(
    () => localStorage.getItem(EMPRESA_FILTRO_KEY) || null
  );

  const setEmpresaFiltroId = useCallback((empresaId: EmpresaFiltroId) => {
    if (empresaId) {
      localStorage.setItem(EMPRESA_FILTRO_KEY, empresaId);
    } else {
      localStorage.removeItem(EMPRESA_FILTRO_KEY);
    }
    setEmpresaFiltroIdState(empresaId);
  }, []);

  // Lista de empresas só é relevante pro superadmin (admin/comum já tem `user.empresas`,
  // que são só as empresas vinculadas a eles).
  useEffect(() => {
    if (user?.tipo !== 'superadmin') {
      setEmpresasDisponiveis([]);
      return;
    }
    empresaService.getAll().then((result) => {
      if (result.success) {
        const ativas = result.data.filter((e) => e.status === 'ativa');
        setEmpresasDisponiveis(ativas);
        // Se o filtro salvo aponta pra uma empresa que não existe mais na lista, limpa
        // (cai em "Todas as empresas") em vez de deixar um id inválido preso.
        setEmpresaFiltroIdState((current) => {
          if (current && !ativas.some((e) => e.id === current)) {
            localStorage.removeItem(EMPRESA_FILTRO_KEY);
            return null;
          }
          return current;
        });
      }
    });
  }, [user?.tipo]);

  usePushNotifications(user?.id);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      Sentry.setTags({
        role: 'anonymous',
        empresa_id: 'none',
        departamento_id: 'none',
      });
      return;
    }

    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.nome,
    });
    Sentry.setTags({
      role: user.tipo,
      empresa_id: user.empresa_id ?? 'none',
      departamento_id: user.departamento_id ?? 'none',
    });
  }, [user]);

  const applyUser = useCallback((validatedUser: AuthUser) => {
    const savedId = localStorage.getItem(EMPRESA_ATIVA_KEY);
    const ativa = resolveEmpresaAtiva(validatedUser, savedId);
    if (ativa) {
      localStorage.setItem(EMPRESA_ATIVA_KEY, ativa.id);
    }
    setUser({
      ...validatedUser,
      // superadmin é papel de plataforma, não de vínculo por empresa (usuario_empresas.tipo
      // só existe como 'admin'/'comum') — nunca deixar o vínculo da empresa ativa rebaixar
      // um superadmin pra 'admin' aqui.
      tipo: validatedUser.tipo === 'superadmin' ? 'superadmin' : (ativa?.tipo ?? validatedUser.tipo),
      empresa_id: ativa?.id ?? validatedUser.empresa_id,
      departamento_id: ativa?.departamento_id ?? validatedUser.departamento_id ?? null,
      modulos: ativa?.modulos ?? validatedUser.modulos ?? [],
    });
    setEmpresaAtiva(ativa);
  }, []);

  const validateStoredToken = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      try {
        const validatedUser = await authService.validateToken(token);
        if (validatedUser) {
          applyUser(validatedUser);
        } else {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(EMPRESA_ATIVA_KEY);
          setUser(null);
          setEmpresaAtiva(null);
        }
      } catch (error) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EMPRESA_ATIVA_KEY);
        setUser(null);
        setEmpresaAtiva(null);
      }
    } else {
      setUser(null);
      setEmpresaAtiva(null);
    }
    setIsLoading(false);
  }, [applyUser]);

  useEffect(() => {
    validateStoredToken();
  }, [validateStoredToken]);

  // Validar token periodicamente (a cada 5 minutos)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        authService.validateToken(token).then((validatedUser) => {
          if (!validatedUser) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(EMPRESA_ATIVA_KEY);
            setUser(null);
            setEmpresaAtiva(null);
          } else {
            applyUser(validatedUser);
          }
        }).catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(EMPRESA_ATIVA_KEY);
          setUser(null);
          setEmpresaAtiva(null);
        });
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, applyUser]);

  const login = async (credentials: LoginCredentials) => {
    const response = await authService.login(credentials);

    if (response.success && response.user && response.token) {
      localStorage.setItem(TOKEN_KEY, response.token);
      applyUser(response.user);
      return { success: true };
    }

    return { success: false, error: response.error };
  };

  const logout = () => {
    // Desvincula o aparelho do push de quem está saindo (funciona mesmo com a sessão já expirada).
    removerTokenPushDoAparelho();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMPRESA_ATIVA_KEY);
    localStorage.removeItem(EMPRESA_FILTRO_KEY);
    setUser(null);
    setEmpresaAtiva(null);
    setEmpresaFiltroIdState(null);
    setEmpresasDisponiveis([]);
  };

  const trocarEmpresa = useCallback((empresaId: string) => {
    if (!user) return;
    const found = user.empresas.find((e) => e.id === empresaId);
    if (!found) return;
    localStorage.setItem(EMPRESA_ATIVA_KEY, empresaId);
    setEmpresaAtiva(found);
    // Atualizar tipo e departamento_id do usuário para refletir contexto da nova empresa
    setUser((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        // superadmin é papel de plataforma, não de vínculo por empresa — mesma proteção
        // de applyUser, senão trocar de empresa pelo seletor rebaixaria o superadmin.
        tipo: prev.tipo === 'superadmin' ? 'superadmin' : found.tipo,
        empresa_id: found.id,
        departamento_id: found.departamento_id ?? null,
        modulos: found.modulos ?? [],
      } as AuthUser;
    });
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        empresaAtiva,
        login,
        logout,
        trocarEmpresa,
        empresasDisponiveis,
        empresaFiltroId,
        setEmpresaFiltroId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
